import { Injectable } from '@angular/core';
import { ShipmentIntakePricing } from '../shipment/models/intake/shipment-intake.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Page } from '../models/page.model';
import { environment } from 'src/environments/environment';
import { ContainerCase } from './models/container-case.model';
import { ContainerCaseResourceLink } from './models/container-case-resource-link.model';
import { ContainerTransportLeg } from './models/container-transport-leg.model';
import { ContainerStorageLeg } from './models/container-storage-leg.model';
import { ContainerEvent } from './models/container-event.model';
import { ContainerDashboardSummary } from './models/container-dashboard-summary.model';
import { ContainerCaseEta } from './models/container-case-eta.model';
import { ContainerCaseReadiness } from './models/container-case-readiness.model';
import { ContainerException } from './models/container-exception.model';
import { ContainerTrackingDashboardOverview } from './models/container-tracking-dashboard-overview.model';
import { ContainerLegHandoff, ContainerLegHandoffContext } from './models/container-leg-handoff.model';
import {
  ContainerJourneyPlan,
  ContainerJourneyPlanItem,
  ContainerJourneyPlanItemUpsertRequest,
  ContainerJourneyPlanView,
  ContainerJourneyTemplateItem,
  ContainerJourneyTemplateView
} from './models/container-journey-plan.model';

export interface ContainerCaseFromShipmentDeliveryRequest {
  containerNumber?: string | null;
  isoEquipmentType?: string | null;
  sealNumber?: string | null;
  bookingNumber?: string | null;
  billOfLadingNumber?: string | null;
  houseBillNumber?: string | null;
  customsDeclarationNumber?: string | null;
  transportMode?: string | null;
  journeyTemplateId?: number | null;
  createdBy?: number | null;
}

export interface ContainerCaseFromShipmentDeliveryResult {
  containerCase?: ContainerCase;
  transportLeg?: ContainerTransportLeg;
  journeyPlan?: ContainerJourneyPlanView;
}

export interface ContainerCaseDeliveryContext {
  containerCase?: ContainerCase;
  deliveryTransportLegs?: ContainerTransportLeg[];
}

export interface ContainerJourneyDispatchResult {
  shipmentId: number;
  shipmentNumber: string;
  deliveryId: number;
  deliveryNumber: string;
  containerCaseIds: number[];
}

export interface ContainerJourneyDispatchCandidate {
  journeyItemId: number;
  containerCaseId: number;
  caseNumber: string;
  containerNumber?: string | null;
  stepName?: string | null;
  plannedStartAt: Date | string;
  plannedEndAt: Date | string;
  vehicleTypeId?: number | null;
}

export interface ContainerBookingIntakeRequest {
  owningCompanyId: number;
  crmClientId?: number | null;
  crmAccountId?: number | null;
  crmClientContactId?: number | null;
  customerCompanyId?: number | null;
  customerContactId?: number | null;
  bookingNumber?: string | null;
  billOfLadingNumber?: string | null;
  houseBillNumber?: string | null;
  customsDeclarationNumber?: string | null;
  numberOfContainers: number;
  defaultIsoEquipmentType?: string | null;
  equipmentJourneyProfileId: number;
  equipmentJourneyProfileCode?: string | null;
  plannedJourneyStartAt?: Date | string | null;
  journeyNotes?: string | null;
  createdBy?: number | null;
  containers: Array<{
    containerNumber?: string | null;
    isoEquipmentType?: string | null;
    sealNumber?: string | null;
  }>;
  journeyBlueprint: ContainerJourneyTemplateItem[];
}

export interface ContainerBookingIntakeResult {
  logisticsProjectId?: number;
  logisticsProjectNumber?: string;
  existingProjectReused?: boolean;
  containerCases?: ContainerCase[];
  journeyPlans?: ContainerJourneyPlanView[];
}

export type ContainerReferenceType = 'BOOKING' | 'BILL_OF_LADING' | 'HOUSE_BILL' | 'CUSTOMS_DECLARATION';

export interface ContainerReferenceSuggestion {
  referenceType: ContainerReferenceType;
  value: string;
  bookingNumber?: string | null;
  billOfLadingNumber?: string | null;
  houseBillNumber?: string | null;
  customsDeclarationNumber?: string | null;
}

export type ContainerBookingWorkspaceReferenceType = 'BOOKING' | 'BILL_OF_LADING';

export interface ContainerBookingWorkspace {
  owningCompanyId: number;
  referenceType: ContainerBookingWorkspaceReferenceType;
  referenceNumber: string;
  bookingNumber?: string | null;
  billOfLadingNumber?: string | null;
  logisticsProjectId?: number | null;
  logisticsProjectNumber?: string | null;
  logisticsProjectName?: string | null;
  crmClientId?: number | null;
  crmAccountId?: number | null;
  crmClientContactId?: number | null;
  customerCompanyId?: number | null;
  customerContactId?: number | null;
  containerCases: ContainerCase[];
}

@Injectable({
  providedIn: 'root'
})
export class ContainerTrackingService {

  private readonly casesUrl = `${environment.restApiUrl}container-tracking/cases`;
  private readonly bookingsUrl = `${environment.restApiUrl}container-tracking/bookings`;
  private readonly dashboardUrl = `${environment.restApiUrl}container-tracking/dashboard`;

  constructor(
    private http: HttpClient
  ) { }

  // --- Lifecycle Case Methods ---

  findCases(params: HttpParams): Observable<Page<ContainerCase>> {
    return this.http.get<Page<ContainerCase>>(this.casesUrl, { params });
  }

  createCase(payload: ContainerCase): Observable<ContainerCase> {
    return this.http.post<ContainerCase>(this.casesUrl, payload);
  }

  createBookingIntake(payload: ContainerBookingIntakeRequest): Observable<ContainerBookingIntakeResult> {
    return this.http.post<ContainerBookingIntakeResult>(`${this.casesUrl}/booking-intake`, payload);
  }

  findReferenceSuggestions(
    owningCompanyId: number,
    referenceType: ContainerReferenceType,
    query = '',
    size = 20
  ): Observable<ContainerReferenceSuggestion[]> {
    const params = new HttpParams()
      .set('owningCompanyId', String(owningCompanyId))
      .set('referenceType', referenceType)
      .set('query', query.trim())
      .set('size', String(size));
    return this.http.get<ContainerReferenceSuggestion[]>(`${this.casesUrl}/reference-suggestions`, { params });
  }

  findBookingWorkspace(
    owningCompanyId: number,
    referenceType: ContainerBookingWorkspaceReferenceType,
    referenceNumber: string
  ): Observable<ContainerBookingWorkspace> {
    const params = new HttpParams()
      .set('owningCompanyId', String(owningCompanyId))
      .set('referenceType', referenceType)
      .set('referenceNumber', referenceNumber.trim());
    return this.http.get<ContainerBookingWorkspace>(`${this.bookingsUrl}/workspace`, { params });
  }

  createCaseFromShipmentDelivery(
    deliveryId: number,
    payload: ContainerCaseFromShipmentDeliveryRequest
  ): Observable<ContainerCaseFromShipmentDeliveryResult> {
    return this.http.post<ContainerCaseFromShipmentDeliveryResult>(
      `${this.casesUrl}/from-shipment-delivery/${deliveryId}`,
      payload
    );
  }

  findCasesForShipmentDelivery(deliveryId: number): Observable<ContainerCaseDeliveryContext[]> {
    return this.http.get<ContainerCaseDeliveryContext[]>(`${this.casesUrl}/from-shipment-delivery/${deliveryId}`);
  }

  findJourneyPlans(caseId: number): Observable<ContainerJourneyPlanView[]> {
    return this.http.get<ContainerJourneyPlanView[]>(`${this.casesUrl}/${caseId}/journey-plans`);
  }

  createJourneyPlan(caseId: number, payload: {
    templateId?: number | null;
    plannedStartAt?: Date | string | null;
    notes?: string | null;
    createdBy?: number | null;
  } = {}): Observable<ContainerJourneyPlanView> {
    return this.http.post<ContainerJourneyPlanView>(`${this.casesUrl}/${caseId}/journey-plans`, payload);
  }

  updateJourneyPlan(caseId: number, planId: number, payload: ContainerJourneyPlan): Observable<ContainerJourneyPlanView> {
    return this.http.put<ContainerJourneyPlanView>(`${this.casesUrl}/${caseId}/journey-plans/${planId}`, payload);
  }

  addJourneyPlanItem(caseId: number, planId: number, payload: ContainerJourneyPlanItemUpsertRequest): Observable<ContainerJourneyPlanItem> {
    return this.http.post<ContainerJourneyPlanItem>(`${this.casesUrl}/${caseId}/journey-plans/${planId}/items`, payload);
  }

  updateJourneyPlanItem(caseId: number, planId: number, itemId: number, payload: ContainerJourneyPlanItemUpsertRequest): Observable<ContainerJourneyPlanItem> {
    return this.http.put<ContainerJourneyPlanItem>(`${this.casesUrl}/${caseId}/journey-plans/${planId}/items/${itemId}`, payload);
  }

  deleteJourneyPlanItem(caseId: number, planId: number, itemId: number): Observable<void> {
    return this.http.delete<void>(`${this.casesUrl}/${caseId}/journey-plans/${planId}/items/${itemId}`);
  }

  confirmJourneyPlanItem(caseId: number, planId: number, itemId: number, confirmedBy?: number | null): Observable<ContainerJourneyPlanView> {
    return this.http.post<ContainerJourneyPlanView>(
      `${this.casesUrl}/${caseId}/journey-plans/${planId}/items/${itemId}/confirm`,
      { confirmedBy }
    );
  }

  sendJourneyItemToPlanning(caseId: number, planId: number, itemId: number): Observable<ContainerJourneyPlanView> {
    return this.http.post<ContainerJourneyPlanView>(
      `${this.casesUrl}/${caseId}/journey-plans/${planId}/items/${itemId}/send-to-planning`, {}
    );
  }

  createTransportDispatch(journeyItemIds: number[], paymentTermId: number, pricing?: ShipmentIntakePricing): Observable<ContainerJourneyDispatchResult> {
    return this.http.post<ContainerJourneyDispatchResult>(
      `${environment.restApiUrl}container-tracking/transport-dispatches`, { journeyItemIds, paymentTermId, pricing }
    );
  }

  findTransportDispatchCandidates(journeyItemId: number): Observable<ContainerJourneyDispatchCandidate[]> {
    return this.http.get<ContainerJourneyDispatchCandidate[]>(
      `${environment.restApiUrl}container-tracking/transport-dispatches/candidates`,
      { params: new HttpParams().set('journeyItemId', String(journeyItemId)) }
    );
  }

  saveJourneyPlanAsTemplate(caseId: number, planId: number, payload: {
    templateCode: string;
    templateName: string;
    description?: string | null;
    journeyType?: string | null;
    applicableEquipmentTypes?: string | null;
    createdBy?: number | null;
  }): Observable<ContainerJourneyTemplateView> {
    return this.http.post<ContainerJourneyTemplateView>(
      `${this.casesUrl}/${caseId}/journey-plans/${planId}/save-as-template`,
      payload
    );
  }

  findJourneyTemplates(owningCompanyId: number): Observable<ContainerJourneyTemplateView[]> {
    return this.http.get<ContainerJourneyTemplateView[]>(`${environment.restApiUrl}container-tracking/journey-templates`, {
      params: new HttpParams().set('owningCompanyId', String(owningCompanyId))
    });
  }

  findCaseById(caseId: number): Observable<ContainerCase> {
    return this.http.get<ContainerCase>(`${this.casesUrl}/${caseId}`);
  }

  updateCase(caseId: number, payload: ContainerCase): Observable<ContainerCase> {
    return this.http.put<ContainerCase>(`${this.casesUrl}/${caseId}`, payload);
  }

  createCaseLink(caseId: number, payload: ContainerCaseResourceLink): Observable<ContainerCaseResourceLink> {
    return this.http.post<ContainerCaseResourceLink>(`${this.casesUrl}/${caseId}/links`, payload);
  }

  findCaseLinks(caseId: number): Observable<ContainerCaseResourceLink[]> {
    return this.http.get<ContainerCaseResourceLink[]>(`${this.casesUrl}/${caseId}/links`);
  }

  updateTransportLeg(caseId: number, legId: number, payload: ContainerTransportLeg): Observable<ContainerTransportLeg> {
    return this.http.put<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}`, payload);
  }

  assignTransportLeg(caseId: number, legId: number, payload: {
    driverId?: number | null;
    vehicleId?: number | null;
    vendorCompanyId?: number | null;
    plannedPickupAt?: Date | string | null;
    plannedDepartureAt?: Date | string | null;
    plannedArrivalAt?: Date | string | null;
    plannedDeliveryAt?: Date | string | null;
    remarks?: string | null;
    updatedBy?: number | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/assign`, payload);
  }

  dispatchTransportLeg(caseId: number, legId: number, payload?: {
    eventTime?: Date | string | null;
    locationId?: number | null;
    locationText?: string | null;
    remarks?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/dispatch`, payload || {});
  }

  arriveTransportLeg(caseId: number, legId: number, payload?: {
    eventTime?: Date | string | null;
    locationId?: number | null;
    locationText?: string | null;
    remarks?: string | null;
    updatedBy?: number | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/arrive`, payload || {});
  }

  deliverTransportLeg(caseId: number, legId: number, payload?: {
    eventTime?: Date | string | null;
    locationId?: number | null;
    locationText?: string | null;
    remarks?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/deliver`, payload || {});
  }

  cancelTransportLeg(caseId: number, legId: number, payload?: {
    eventTime?: Date | string | null;
    locationId?: number | null;
    locationText?: string | null;
    remarks?: string | null;
    updatedBy?: number | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/cancel`, payload || {});
  }

  exceptionTransportLeg(caseId: number, legId: number, payload: {
    eventTime?: Date | string | null;
    exceptionReason?: string | null;
    remarks?: string | null;
    updatedBy?: number | null;
  }): Observable<ContainerTransportLeg> {
    return this.http.post<ContainerTransportLeg>(`${this.casesUrl}/${caseId}/transport-legs/${legId}/exception`, payload);
  }

  createStorageLeg(caseId: number, payload: ContainerStorageLeg): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs`, payload);
  }

  updateStorageLeg(caseId: number, storageLegId: number, payload: ContainerStorageLeg): Observable<ContainerStorageLeg> {
    return this.http.put<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}`, payload);
  }

  receiveStorageLeg(caseId: number, storageLegId: number, payload?: {
    eventTime?: Date | string | null;
    facilityName?: string | null;
    notes?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}/receive`, payload || {});
  }

  assignStorageSlot(caseId: number, storageLegId: number, payload: {
    zoneId?: number | null;
    slotCode?: string | null;
    notes?: string | null;
    updatedBy?: number | null;
  }): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}/slot`, payload);
  }

  holdStorageLeg(caseId: number, storageLegId: number, payload?: {
    eventTime?: Date | string | null;
    notes?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}/hold`, payload || {});
  }

  releaseStorageLeg(caseId: number, storageLegId: number, payload?: {
    eventTime?: Date | string | null;
    notes?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}/release`, payload || {});
  }

  exitStorageLeg(caseId: number, storageLegId: number, payload?: {
    eventTime?: Date | string | null;
    notes?: string | null;
    updatedBy?: number | null;
    handoff?: ContainerLegHandoffContext | null;
  }): Observable<ContainerStorageLeg> {
    return this.http.post<ContainerStorageLeg>(`${this.casesUrl}/${caseId}/storage-legs/${storageLegId}/exit`, payload || {});
  }

  createEvent(caseId: number, payload: ContainerEvent): Observable<ContainerEvent> {
    return this.http.post<ContainerEvent>(`${this.casesUrl}/${caseId}/events`, payload);
  }

  findTimeline(caseId: number): Observable<ContainerEvent[]> {
    return this.http.get<ContainerEvent[]>(`${this.casesUrl}/${caseId}/timeline`);
  }

  findTransportLegs(caseId: number): Observable<ContainerTransportLeg[]> {
    return this.http.get<ContainerTransportLeg[]>(`${this.casesUrl}/${caseId}/transport-legs`);
  }

  findStorageLegs(caseId: number): Observable<ContainerStorageLeg[]> {
    return this.http.get<ContainerStorageLeg[]>(`${this.casesUrl}/${caseId}/storage-legs`);
  }

  findHandoffs(caseId: number): Observable<ContainerLegHandoff[]> {
    return this.http.get<ContainerLegHandoff[]>(`${this.casesUrl}/${caseId}/handoffs`);
  }

  reconcileHandoffs(caseId: number): Observable<ContainerLegHandoff[]> {
    return this.http.post<ContainerLegHandoff[]>(`${this.casesUrl}/${caseId}/handoffs/reconcile`, {});
  }

  getCaseEta(caseId: number): Observable<ContainerCaseEta> {
    return this.http.get<ContainerCaseEta>(`${this.casesUrl}/${caseId}/eta`);
  }

  getCaseReadiness(caseId: number): Observable<ContainerCaseReadiness> {
    return this.http.get<ContainerCaseReadiness>(`${this.casesUrl}/${caseId}/readiness`);
  }

  getCaseExceptions(caseId: number): Observable<ContainerException[]> {
    return this.http.get<ContainerException[]>(`${this.casesUrl}/${caseId}/exceptions`);
  }

  closeCase(caseId: number, payload?: { overrideReason?: string; closedBy?: number }): Observable<ContainerCase> {
    return this.http.post<ContainerCase>(`${this.casesUrl}/${caseId}/close`, payload || {});
  }

  getDashboardSummary(companyId: number): Observable<ContainerDashboardSummary> {
    return this.http.get<ContainerDashboardSummary>(`${environment.restApiUrl}container-tracking/dashboard/summary`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  getDashboardExceptions(companyId: number): Observable<ContainerException[]> {
    return this.http.get<ContainerException[]>(`${environment.restApiUrl}container-tracking/dashboard/exceptions`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  getDashboardActiveCases(companyId: number): Observable<ContainerCase[]> {
    return this.http.get<ContainerCase[]>(`${environment.restApiUrl}container-tracking/dashboard/active-cases`, {
      params: new HttpParams().set('companyId', String(companyId))
    });
  }

  getDashboardOverview(params: HttpParams): Observable<ContainerTrackingDashboardOverview> {
    return this.http.get<ContainerTrackingDashboardOverview>(`${this.dashboardUrl}/overview`, { params });
  }

}
